import { lazy, useMemo } from 'react';
import styles from './Dashboard.module.css';
import Summary from '@/components/Dashboard/Summary';
import { OnboardingFeatureKey } from '@gokrew/models/company';
import { useModuleEnabled } from '@/hooks/useModuleEnabled';
import { useTour } from '@gokrew/ui/hooks';
import { buildDashboardTourVariant } from '@/hooks/tours/tourVariants';
import { PermissionGuard } from '@/components/PermissionGuard';
import { permissionKeys } from '@/utility';
import { usePermissions } from '@/context/PermissionContext';

const TourGuide = lazy(() =>
  import('@gokrew/ui/components/TourGuide/TourGuide').then((m) => ({
    default: m.TourGuide,
  }))
);
const OnLeave = lazy(() => import('@/components/Dashboard/OnLeave'));
const NeedsAttention = lazy(
  () => import('@/components/Dashboard/NeedsAttention')
);
const AssetsOverview = lazy(
  () => import('@/components/Dashboard/AssetsOverview')
);
const Birthdays = lazy(() => import('@/components/Dashboard/Birthdays'));

/** Scopes that each bring a kind of row into the feed (spec D69). */
const ATTENTION_SCOPES = [
  permissionKeys.leave_request.read,
  permissionKeys.employee.read,
  permissionKeys.task_management.read,
  permissionKeys.recruiting.read,
  permissionKeys.employee_document.read,
  // Profile change requests to decide (change rules, 2026-10-21).
  permissionKeys.employee.update,
];

export function DashboardPage() {
  const isModuleEnabled = useModuleEnabled();
  const { permissions, fetchingPermissions } = usePermissions();
  // A step is only reachable while its card renders, so gate each one on the
  // same scope that card's PermissionGuard uses below.
  const hasOnLeaveCard = permissions.has(permissionKeys.leave_request.read);
  const hasBirthdaysCard = permissions.has(permissionKeys.employee.read);
  // The feed shows whatever the viewer may act on: any one of these scopes
  // gets it, and the server keeps only the item types they hold (D69).
  const hasAttentionCard = ATTENTION_SCOPES.some((scope) =>
    permissions.has(scope)
  );
  const hasAssetsCard =
    isModuleEnabled(OnboardingFeatureKey.Assets) &&
    permissions.has(permissionKeys.asset.read);
  const dashboardTourVariant = useMemo(
    () =>
      buildDashboardTourVariant({
        hasOnLeaveCard,
        hasAttentionCard,
        hasAssetsCard,
        hasBirthdaysCard,
      }),
    [hasOnLeaveCard, hasAttentionCard, hasAssetsCard, hasBirthdaysCard]
  );
  // Hold auto-start until permissions resolve so the step list (gated on
  // hasOnLeaveCard/hasAttentionCard) can't change shape right as the tour
  // arms. That race was suppressing the first-step beacon for new viewers.
  const { steps, run, skipBeacon, onFinish } = useTour(dashboardTourVariant, {
    enabled: !fetchingPermissions,
  });

  return (
    <div className={styles.main}>
      <TourGuide
        steps={steps}
        run={run}
        skipBeacon={skipBeacon}
        onFinish={onFinish}
      />
      <Summary />

      {(hasAttentionCard || hasOnLeaveCard || hasBirthdaysCard) && (
        <div className={styles.rowDuoStack}>
          {hasAttentionCard && (
            <div className={`${styles.stack} ${styles.wide}`}>
              {/* Needs attention is the whole wide column, at full height:
                  it shows as many rows as fit (JP, 2026-10-04). */}
              <div className={styles.attention}>
                <NeedsAttention />
              </div>
            </div>
          )}
          {(hasOnLeaveCard || hasBirthdaysCard) && (
            <div className={`${styles.stack} ${styles.narrow}`}>
              <PermissionGuard scope={permissionKeys.leave_request.read}>
                <div className={styles.away}>
                  <OnLeave />
                </div>
              </PermissionGuard>
              <PermissionGuard scope={permissionKeys.employee.read}>
                <div className={styles.birthdays}>
                  <Birthdays />
                </div>
              </PermissionGuard>
            </div>
          )}
        </div>
      )}

      {isModuleEnabled(OnboardingFeatureKey.Assets) && (
        <PermissionGuard scope={permissionKeys.asset.read}>
          <AssetsOverview />
        </PermissionGuard>
      )}
    </div>
  );
}
